/**
 * useStudioMedia — getUserMedia + RTCPeerConnection wired through DO WS
 * signaling for trainer ↔ case video call.
 *
 * Convention: trainer is the WebRTC "offerer" once the case peer joins.
 * Case is the answerer. Either side can renegotiate on tab refresh —
 * onnegotiationneeded triggers re-offer.
 *
 * STUN-only (Google public). Falls back gracefully if NAT hostile —
 * users get "couldn't connect peer video" inline message; control
 * channel keeps working over DO WS.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Role, ServerMsg, ClientMsg } from "./useStudioRoom.js";

const STUN_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

// localStorage keys for trainer / case device picks (per-role for shared
// kiosk machines so trainer + case selections don't overwrite each other).
const VIDEO_DEVICE_KEY = "cognihome:videoDeviceId";
const AUDIO_DEVICE_KEY = "cognihome:audioDeviceId";

function readDeviceId(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

export interface MediaDeviceLite {
  deviceId: string;
  label:    string;
}

interface UseStudioMediaOptions {
  role: Role;
  bothPresent: boolean;             // when both peers are in room → start handshake
  send: (msg: ClientMsg) => void;
  onSignal: (handler: (msg: ServerMsg) => void) => void;
}

export function useStudioMedia({ role, bothPresent, send, onSignal }: UseStudioMediaOptions) {
  const [localStream,  setLocalStream]  = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [mediaError, setMediaError]     = useState<string | null>(null);
  const [peerError,  setPeerError]      = useState<string | null>(null);

  // P8-1: device-selection state. null = browser default device. Persists
  // across sessions so user doesn't re-pick every time.
  const [videoDeviceId, _setVideoDeviceId] = useState<string | null>(() => readDeviceId(VIDEO_DEVICE_KEY));
  const [audioDeviceId, _setAudioDeviceId] = useState<string | null>(() => readDeviceId(AUDIO_DEVICE_KEY));
  const [videoDevices,  setVideoDevices]   = useState<MediaDeviceLite[]>([]);
  const [audioDevices,  setAudioDevices]   = useState<MediaDeviceLite[]>([]);

  const pcRef                 = useRef<RTCPeerConnection | null>(null);
  const screenSenderRef       = useRef<RTCRtpSender | null>(null);
  const webcamVideoSenderRef  = useRef<RTCRtpSender | null>(null);
  const webcamAudioSenderRef  = useRef<RTCRtpSender | null>(null);
  const remoteStreamIdRef     = useRef<string | null>(null);
  const startedRef            = useRef(false);

  const setVideoDeviceId = useCallback((id: string | null) => {
    _setVideoDeviceId(id);
    try {
      if (id) localStorage.setItem(VIDEO_DEVICE_KEY, id);
      else    localStorage.removeItem(VIDEO_DEVICE_KEY);
    } catch { /* */ }
  }, []);
  const setAudioDeviceId = useCallback((id: string | null) => {
    _setAudioDeviceId(id);
    try {
      if (id) localStorage.setItem(AUDIO_DEVICE_KEY, id);
      else    localStorage.removeItem(AUDIO_DEVICE_KEY);
    } catch { /* */ }
  }, []);

  const refreshDevices = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setVideoDevices(list
        .filter(d => d.kind === "videoinput")
        .map(d => ({ deviceId: d.deviceId, label: d.label || "(未命名攝影機)" })));
      setAudioDevices(list
        .filter(d => d.kind === "audioinput")
        .map(d => ({ deviceId: d.deviceId, label: d.label || "(未命名麥克風)" })));
    } catch { /* */ }
  }, []);

  // ── 1. Acquire / re-acquire local media ───────────────────────────
  // Re-runs on device change. After first stream live-swap track on
  // existing pc senders to avoid SDP renegotiation; if pc not built
  // yet (lobby phase) just store the stream and let the build effect
  // pick it up.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const constraints: MediaStreamConstraints = {
          video: videoDeviceId
            ? { deviceId: { exact: videoDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
            : { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: audioDeviceId
            ? { deviceId: { exact: audioDeviceId } }
            : true,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        setLocalStream((prev) => {
          // Stop previous stream's tracks AFTER capturing the new stream
          // so the user never sees a black gap.
          if (prev) prev.getTracks().forEach(t => t.stop());
          return stream;
        });
        setMediaError(null);
        // Live-swap on existing pc senders (mid-lobby device change OR
        // mid-session). replaceTrack avoids SDP round-trip.
        const newVideo = stream.getVideoTracks()[0] ?? null;
        const newAudio = stream.getAudioTracks()[0] ?? null;
        if (webcamVideoSenderRef.current && newVideo) {
          try { await webcamVideoSenderRef.current.replaceTrack(newVideo); } catch { /* */ }
        }
        if (webcamAudioSenderRef.current && newAudio) {
          try { await webcamAudioSenderRef.current.replaceTrack(newAudio); } catch { /* */ }
        }
        // Browsers gate device labels behind getUserMedia consent; refresh now.
        refreshDevices();
      } catch (err) {
        const e = err as DOMException;
        setMediaError(
          e.name === "NotAllowedError"      ? "請允許瀏覽器使用攝影機與麥克風"
          : e.name === "NotFoundError"      ? "找不到攝影機或麥克風"
          : e.name === "OverconstrainedError" ? "選定的裝置已不可用，請改選"
          : `${e.name}: ${e.message}`
        );
      }
    })();
    return () => { cancelled = true; };
  }, [videoDeviceId, audioDeviceId, refreshDevices]);

  // ── 1b. Re-enumerate on hot-plug ──────────────────────────────────
  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md || !md.addEventListener) return;
    md.addEventListener("devicechange", refreshDevices);
    return () => md.removeEventListener("devicechange", refreshDevices);
  }, [refreshDevices]);

  // ── 2. Receive WS signaling messages ──────────────────────────────
  useEffect(() => {
    onSignal(async (msg) => {
      const pc = pcRef.current;
      if (!pc) return;
      try {
        if (msg.type === "webrtc_offer") {
          await pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send({ type: "webrtc_answer", sdp: answer.sdp! });
        } else if (msg.type === "webrtc_answer") {
          await pc.setRemoteDescription({ type: "answer", sdp: msg.sdp });
        } else if (msg.type === "ice_candidate") {
          if (msg.candidate) {
            await pc.addIceCandidate(msg.candidate as RTCIceCandidateInit);
          }
        }
      } catch (e) {
        const err = e as Error;
        setPeerError(`signaling failed: ${err.message}`);
      }
    });
  }, [onSignal, send]);

  // ── 3. Build / rebuild RTCPeerConnection ──────────────────────────
  // P7 (M3 audit): when `bothPresent` flips false (case tab reload /
  // crash) the previously-built pc dies but pcRef.current still points
  // at it, so when case rejoins the original effect short-circuited and
  // trainer's video stayed frozen. Tear down on bothPresent=false, then
  // rebuild the moment it flips back true. Rebuilding produces a fresh
  // SDP exchange so ICE re-runs end-to-end.
  useEffect(() => {
    if (!localStream || !bothPresent) {
      if (pcRef.current) {
        try { pcRef.current.close(); } catch { /* */ }
        pcRef.current = null;
        screenSenderRef.current = null;
        webcamVideoSenderRef.current = null;
        webcamAudioSenderRef.current = null;
        remoteStreamIdRef.current = null;
        startedRef.current = false;
        // Keep peerError visible until peer rejoins so trainer sees what
        // happened, but clear remote streams so the tile doesn't show a
        // frozen last frame.
        setRemoteStream(null);
        setScreenStream(null);
      }
      return;
    }
    if (pcRef.current) return;     // already built for this bothPresent=true session

    const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS });
    pcRef.current = pc;
    setPeerError(null);            // fresh attempt, clear any prior failure

    // 1st m-line: webcam audio + video. We tag the local stream so the
    // peer's ontrack receives a stream id we can match against the
    // 2nd transceiver below. Capture the senders into refs so device
    // picker swaps (P8-1) can replaceTrack mid-session.
    for (const t of localStream.getTracks()) {
      const sender = pc.addTrack(t, localStream);
      if (t.kind === "video") webcamVideoSenderRef.current = sender;
      if (t.kind === "audio") webcamAudioSenderRef.current = sender;
    }

    // 2nd m-line: case-side game-canvas screen mirror (spec §6.1 +
    // line 65 game-canvas-via-WebRTC). Pre-allocate a sendonly /
    // recvonly transceiver so the SDP carries an extra m-line from
    // the very first offer; once the case has its mirror canvas ready
    // it calls setScreenTrack(track) to replaceTrack on this sender —
    // no renegotiation needed.
    const screenT = pc.addTransceiver("video", {
      direction: role === "case" ? "sendonly" : "recvonly",
    });
    screenSenderRef.current = screenT.sender;
    const screenTransceiverRef = screenT;

    pc.ontrack = (ev) => {
      // Match by transceiver identity — the webcam tracks come in on
      // the addTrack-created transceivers (mid 0 + 1), the screen track
      // comes in on the explicit screen transceiver. Stream-id ordering
      // is implementation-defined (Safari may swap), so identity check
      // is reliable across browsers.
      console.debug("[useStudioMedia] ontrack", ev.track.kind, "stream", ev.streams[0]?.id);
      const [stream] = ev.streams;
      if (!stream) return;
      if (ev.transceiver === screenTransceiverRef) {
        // Wrap in a fresh MediaStream so React re-renders even if the
        // browser keeps the same stream object across ontrack callbacks.
        setScreenStream(new MediaStream(stream.getTracks()));
      } else {
        // Webcam pair (audio + video). Two ontrack callbacks fire — once
        // for each track. The browser delivers the SAME MediaStream object
        // both times, so React.setState-ing it twice would skip the second
        // render (referential equality) and the <video srcObject=stream>
        // wouldn't pick up the second track. Clone the stream into a NEW
        // MediaStream each time so React always re-renders and the video
        // element re-binds with the latest track set. This was a one-way
        // audio bug fixed 2026-05-09.
        if (!remoteStreamIdRef.current) remoteStreamIdRef.current = stream.id;
        setRemoteStream(new MediaStream(stream.getTracks()));
      }
    };

    pc.onicecandidate = (ev) => {
      if (ev.candidate) send({ type: "ice_candidate", candidate: ev.candidate.toJSON() });
    };

    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "failed") {
        setPeerError("peer connection failed (NAT). 控制 channel 仍可用。");
      } else if (s === "disconnected") {
        // Transient disconnect — give it ~10 s, then escalate. The
        // bothPresent toggle from the WS layer handles real reconnects.
        setPeerError("peer 暫時失聯，等候 ICE 重試…");
      } else if (s === "connected") {
        setPeerError(null);
      }
    };

    pc.onnegotiationneeded = async () => {
      if (role !== "trainer") return; // trainer is offerer
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        send({ type: "webrtc_offer", sdp: offer.sdp! });
      } catch (e) {
        const err = e as Error;
        setPeerError(`offer failed: ${err.message}`);
      }
    };

    // For trainer: explicitly trigger initial offer (negotiationneeded fires
    // anyway when tracks are added, but be explicit to handle race)
    if (role === "trainer" && !startedRef.current) {
      startedRef.current = true;
      // negotiationneeded will fire from track adds above; nothing to do here
    }

    return () => {
      // Rebuild on next dependency change (bothPresent toggle) — close
      // this pc so we don't leak. The build branch above will create a
      // fresh one when bothPresent comes back true.
      try { pc.close(); } catch { /* */ }
      if (pcRef.current === pc) pcRef.current = null;
    };
  }, [localStream, bothPresent, role, send]);

  // ── 4. Cleanup ────────────────────────────────────────────────────
  // The previous version captured `localStream` in the closure at mount
  // time (always null), so on unmount the webcam tracks were never
  // stopped — LED stayed on after leaving the Studio. Use a ref so
  // the cleanup reads the current stream at unmount time.
  const localStreamRef = useRef(localStream);
  localStreamRef.current = localStream;
  useEffect(() => {
    return () => {
      pcRef.current?.close();
      pcRef.current = null;
      localStreamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  // Case-side: install a video track onto the pre-allocated screen
  // transceiver. Pass null to clear (e.g. iframe unloaded). Caller is
  // responsible for the underlying MediaStream lifetime.
  const setScreenTrack = useCallback(async (track: MediaStreamTrack | null) => {
    const sender = screenSenderRef.current;
    if (!sender) return;
    try {
      await sender.replaceTrack(track);
    } catch (e) {
      const err = e as Error;
      setPeerError(`screen track install failed: ${err.message}`);
    }
  }, []);

  return {
    localStream, remoteStream, screenStream,
    mediaError, peerError, setScreenTrack,
    // P8-1 device picker
    videoDevices, audioDevices,
    videoDeviceId, audioDeviceId,
    setVideoDeviceId, setAudioDeviceId,
  };
}
